import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import {
  Users,
  UserPlus,
  Mail,
  Shield,
  Calendar,
  Search,
  Loader,
  AlertCircle,
  CheckCircle,
  XCircle,
  Edit,
  Trash2,
  Ban
} from 'lucide-react';

const AdminManageUsers = () => {
  const { isDarkMode } = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('active'); // active, suspended, all
  const [editingUser, setEditingUser] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  const adminData = JSON.parse(sessionStorage.getItem('adminData') || '{}');

  useEffect(() => {
    fetchUsers();
  }, [filter]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`https://api.fsgu.co.za/admin-users.php?status=${filter}`);
      const data = await response.json();

      if (data.success) {
        setUsers(data.data || []);
      } else {
        setError(data.message || 'Failed to load users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.display_name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.role?.toLowerCase().includes(searchLower)
    );
  });

  const handleSuspend = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    const action = newStatus === 'suspended' ? 'suspend' : 'reactivate';
    
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    setProcessingId(userId);

    try {
      const response = await fetch('https://api.fsgu.co.za/update-admin-status.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          status: newStatus,
          updated_by: adminData.id
        })
      });

      const data = await response.json();

      if (data.success) {
        fetchUsers();
        alert(`User ${action}d successfully`);
      } else {
        alert('Error: ' + (data.message || `Failed to ${action} user`));
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert(`Failed to ${action} user`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (userId, email) => {
    const user = users.find(u => u.id === userId);
    if (user?.role === 'dev') {
      alert('Cannot delete dev users - they are protected');
      return;
    }

    const confirmed = window.prompt(
      `⚠️ WARNING: This will permanently delete this user.\n\nType "${email}" to confirm deletion:`
    );

    if (confirmed !== email) {
      return;
    }

    setProcessingId(userId);

    try {
      const response = await fetch('https://api.fsgu.co.za/delete-admin.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          deleted_by: adminData.id
        })
      });

      const data = await response.json();

      if (data.success) {
        fetchUsers();
        alert('User deleted successfully');
      } else {
        alert('Error: ' + (data.message || 'Failed to delete user'));
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Failed to delete user');
    } finally {
      setProcessingId(null);
    }
  };

  const handleEditRole = async (userId, newRole) => {
    setProcessingId(userId);

    try {
      const response = await fetch('https://api.fsgu.co.za/update-admin-role.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          role: newRole,
          updated_by: adminData.id
        })
      });

      const data = await response.json();

      if (data.success) {
        fetchUsers();
        setEditingUser(null);
        alert('Role updated successfully');
      } else {
        alert('Error: ' + (data.message || 'Failed to update role'));
      }
    } catch (err) {
      console.error('Error updating role:', err);
      alert('Failed to update role');
    } finally {
      setProcessingId(null);
    }
  };

  const getRoleBadge = (role) => {
    const roleInfo = {
      dev: { 
        text: '🔧 Dev', 
        class: isDarkMode 
          ? 'bg-gradient-to-r from-fsgu-blue-500/30 to-fsgu-green-500/30 text-fsgu-blue-400 border-fsgu-blue-500/50' 
          : 'bg-gradient-to-r from-fsgu-blue-100 to-fsgu-green-100 text-fsgu-blue-700 border-fsgu-blue-300'
      },
      super_admin: { 
        text: 'Super Admin', 
        class: isDarkMode 
          ? 'bg-fsgu-green-900/30 text-fsgu-green-400 border-fsgu-green-500/50' 
          : 'bg-fsgu-green-100 text-fsgu-green-700 border-fsgu-green-300'
      },
      admin: { 
        text: 'Admin', 
        class: isDarkMode 
          ? 'bg-fsgu-blue-900/30 text-fsgu-blue-400 border-fsgu-blue-500/50' 
          : 'bg-fsgu-blue-100 text-fsgu-blue-700 border-fsgu-blue-300'
      },
      editor: { 
        text: 'Editor', 
        class: isDarkMode 
          ? 'bg-fsgu-gray-700 text-fsgu-silver-400 border-fsgu-gray-600' 
          : 'bg-fsgu-silver-200 text-fsgu-silver-700 border-fsgu-silver-300'
      }
    };

    const info = roleInfo[role] || roleInfo.editor;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${info.class}`}>
        <Shield className="w-3 h-3" />
        {info.text}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
          isDarkMode
            ? 'bg-fsgu-green-900/30 text-fsgu-green-400 border-fsgu-green-500/50'
            : 'bg-fsgu-green-100 text-fsgu-green-700 border-fsgu-green-300'
        }`}>
          <CheckCircle className="w-3 h-3" />
          Active
        </span>
      );
    }
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
        isDarkMode
          ? 'bg-fsgu-red-900/30 text-fsgu-red-400 border-fsgu-red-500/50'
          : 'bg-fsgu-red-100 text-fsgu-red-700 border-fsgu-red-300'
      }`}>
        <XCircle className="w-3 h-3" />
        Suspended
      </span>
    );
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
      }`}>
        <div className="text-center">
          <div className={`inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent mb-4 ${
            isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
          }`}></div>
          <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
            Loading users...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
    }`}>
      <div className="container-custom py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-3xl sm:text-4xl font-bold font-cursive ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              Admin Users
            </h1>
            <p className={`text-sm mt-1 ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>
              Manage FSGU staff admin accounts
            </p>
          </div>

          <Link
            to="/admin/users/create"
            className="btn btn-primary inline-flex items-center justify-center"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            Create User
          </Link>
        </div>

        {/* Filters & Search */}
        <div className={`p-6 rounded-xl border ${
          isDarkMode 
            ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
            : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
        }`}>
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Filter Buttons */}
            <div className="flex gap-2">
              {['active', 'suspended', 'all'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    filter === status
                      ? isDarkMode
                        ? 'bg-fsgu-green-500 text-white'
                        : 'bg-fsgu-green-500 text-white'
                      : isDarkMode
                      ? 'bg-fsgu-gray-700 text-fsgu-silver-400 hover:bg-fsgu-gray-600'
                      : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 pointer-events-none ${
                  isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                }`} />
                <input
                  type="text"
                  placeholder="Search by name, email, or role..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10 w-full"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className={`p-4 rounded-xl flex items-start gap-3 ${
            isDarkMode
              ? 'bg-fsgu-red-900/30 border border-fsgu-red-700/50'
              : 'bg-fsgu-red-50 border border-fsgu-red-200'
          }`}>
            <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              isDarkMode ? 'text-fsgu-red-400' : 'text-fsgu-red-600'
            }`} />
            <p className={`text-sm ${
              isDarkMode ? 'text-fsgu-red-300' : 'text-fsgu-red-700'
            }`}>
              {error}
            </p>
          </div>
        )}

        {/* Users List */}
        {filteredUsers.length === 0 ? (
          <div className={`text-center py-16 rounded-xl border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <Users className={`w-20 h-20 mx-auto mb-4 ${
              isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-400'
            }`} />
            <h3 className={`text-xl font-semibold mb-2 ${
              isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
            }`}>
              No users found
            </h3>
            <p className={`text-sm ${
              isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
            }`}>
              {searchTerm ? 'Try adjusting your search' : 'No users match the selected filter'}
            </p>
          </div>
        ) : (
          <div className="grid gap-6">
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                className={`rounded-xl p-6 border transition-all hover:shadow-lg ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 border-fsgu-gray-700 hover:border-fsgu-green-500/50'
                    : 'bg-fsgu-white border-fsgu-silver-200 hover:border-fsgu-green-500/50 shadow-sm'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  {/* User Info */}
                  <div className="flex-1 space-y-4">
                    <div>
                      <h3 className={`text-xl font-bold mb-2 ${
                        isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                      }`}>
                        {user.display_name || 'No name'}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {getRoleBadge(user.role)}
                        {getStatusBadge(user.status)}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <Mail className={`w-4 h-4 flex-shrink-0 ${
                          isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                        }`} />
                        <span className={`text-sm truncate ${
                          isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                        }`}>
                          {user.email}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Calendar className={`w-4 h-4 flex-shrink-0 ${
                          isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                        }`} />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                        }`}>
                          Created: {new Date(user.created_at).toLocaleDateString()}
                        </span>
                      </div>

                      {user.last_login && (
                        <div className="flex items-center gap-2">
                          <Calendar className={`w-4 h-4 flex-shrink-0 ${
                            isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                          }`} />
                          <span className={`text-sm ${
                            isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                          }`}>
                            Last login: {new Date(user.last_login).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {user.role !== 'dev' && (adminData.role === 'super_admin' || adminData.role === 'dev') && (
                    <div className="flex flex-col gap-2 min-w-[140px]">
                      {editingUser === user.id ? (
                        <div className="flex flex-col gap-2">
                          <select
                            value={user.role}
                            onChange={(e) => handleEditRole(user.id, e.target.value)}
                            disabled={processingId === user.id}
                            className={`input text-sm ${
                              processingId === user.id ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          >
                            <option value="editor">Editor</option>
                            <option value="admin">Admin</option>
                            {adminData.role === 'dev' && <option value="super_admin">Super Admin</option>}
                          </select>
                          <button
                            onClick={() => setEditingUser(null)}
                            className="btn btn-outline text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => setEditingUser(user.id)}
                            disabled={processingId === user.id}
                            className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              processingId === user.id
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                            } ${
                              isDarkMode
                                ? 'bg-fsgu-blue-500/20 text-fsgu-blue-400 hover:bg-fsgu-blue-500/30'
                                : 'bg-fsgu-blue-100 text-fsgu-blue-700 hover:bg-fsgu-blue-200'
                            }`}
                          >
                            <Edit className="w-4 h-4" />
                            Edit Role
                          </button>

                          <button
                            onClick={() => handleSuspend(user.id, user.status)}
                            disabled={processingId === user.id}
                            className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              processingId === user.id
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                            } ${
                              user.status === 'active'
                                ? isDarkMode
                                  ? 'bg-fsgu-gold-900/30 text-fsgu-gold-400 hover:bg-fsgu-gold-900/40'
                                  : 'bg-fsgu-gold-100 text-fsgu-gold-700 hover:bg-fsgu-gold-200'
                                : isDarkMode
                                  ? 'bg-fsgu-green-900/30 text-fsgu-green-400 hover:bg-fsgu-green-900/40'
                                  : 'bg-fsgu-green-100 text-fsgu-green-700 hover:bg-fsgu-green-200'
                            }`}
                          >
                            <Ban className="w-4 h-4" />
                            {user.status === 'active' ? 'Suspend' : 'Reactivate'}
                          </button>

                          <button
                            onClick={() => handleDelete(user.id, user.email)}
                            disabled={processingId === user.id}
                            className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              processingId === user.id
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                            } ${
                              isDarkMode
                                ? 'bg-fsgu-red-900/30 text-fsgu-red-400 hover:bg-fsgu-red-900/40'
                                : 'bg-fsgu-red-100 text-fsgu-red-700 hover:bg-fsgu-red-200'
                            }`}
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminManageUsers;